import { Container, Button, TextField, Box, Select, MenuItem, Card, CardContent, InputAdornment, IconButton, Typography, Avatar, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import LogTheme from '../Layout/LogTheme';
import { useAuth } from '../../contexts/AuthContext';
import ImageUploader from '../Layout/ImageUploader';
import axios from 'axios';
import { validatePassword, getPasswordStrength } from '../../utils/passwordValidation';
import { getAuthHeader, getAuthHeaders, getImageDisplayUrl, getUploadApiBaseUrl } from '../../utils/api';
import { useLogbook } from '../../contexts/LogbookContext';

function ChangeProfile() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState(''); // Nytt
  const [lastName, setLastName] = useState(''); // Nytt
  const [bio, setBio] = useState(''); // Nytt
  const [profilePictures, setProfilePictures] = useState(null);
  const { userid } = useParams();
  const currentUser = useAuth();
  const navigate = useNavigate();
  const [imagesToRemove, setImagesToRemove] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [passwordError, setPasswordError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { logbooks, refreshLogbooks } = useLogbook();
  const ownedLogbooks = (logbooks || []).filter((lb) => lb.isOwner);
  const [logbookNames, setLogbookNames] = useState({});
  const [savingLogbookId, setSavingLogbookId] = useState(null);
  const [logbookError, setLogbookError] = useState(null);
  const [logbookSuccess, setLogbookSuccess] = useState(null);
  const [logbookDeleteDialogOpen, setLogbookDeleteDialogOpen] = useState(false);
  const [logbookDeleteTargetId, setLogbookDeleteTargetId] = useState(null);
  const [logbookDeletePreview, setLogbookDeletePreview] = useState(null);
  const [logbookDeletePreviewLoading, setLogbookDeletePreviewLoading] = useState(false);
  const [logbookDeletePreviewError, setLogbookDeletePreviewError] = useState(null);
  const [logbookDeletingId, setLogbookDeletingId] = useState(null);


  useEffect(() => {
    if (userid !== currentUser.userId && currentUser.role !== 'admin') {
      // Om userId inte är tillgängligt, vänta på nästa render för att göra fetch-anropet
      navigate('/info');
      return;
  }
    console.log('userid i ChangeProfile: ', userid);

    fetch(`${process.env.REACT_APP_SERVER_URL}/api/showprofile/${userid}`)
      .then(response => response.json())
      .then(data => {
        // Sätt state med data från servern
        setUsername(data.username);
        setEmail(data.email);
        setFirstName(data.firstName || '');
        setLastName(data.lastName || '');
        setBio(data.bio || '');
        // Hantera profilePictures - kan vara array, null eller undefined
        let profilePicturesArray = data.profilePictures || [];
        if (typeof profilePicturesArray === 'string') {
          try {
            profilePicturesArray = JSON.parse(profilePicturesArray);
          } catch (e) {
            profilePicturesArray = [];
          }
        }
        if (!Array.isArray(profilePicturesArray)) {
          profilePicturesArray = [];
        }
        setProfilePictures(profilePicturesArray);
      })
      .catch(error => {
        console.error('Error:', error);
      });
  }, [userid]); // Kör useEffect när `userId` ändras

  const ownedLogbookIdsStr = (ownedLogbooks || []).map((lb) => lb._id).join(',');
  useEffect(() => {
    setLogbookNames((prev) => {
      const next = {};
      ownedLogbooks.forEach((lb) => {
        next[lb._id] = prev[lb._id] !== undefined ? prev[lb._id] : (lb.name || '');
      });
      return next;
    });
  }, [ownedLogbookIdsStr]);

  const handleLogbookNameChange = (logbookId, value) => {
    setLogbookNames((prev) => ({ ...prev, [logbookId]: value }));
    setLogbookError(null);
  };

  const handleSaveLogbookName = async (logbookId) => {
    setLogbookError(null);
    setSavingLogbookId(logbookId);
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/logbooks/${logbookId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name: (logbookNames[logbookId] || '').trim() || null })
      });
      if (res.ok) {
        await refreshLogbooks();
      } else {
        const data = await res.json().catch(() => ({}));
        setLogbookError(data.message || 'Kunde inte spara namnet.');
      }
    } catch (err) {
      setLogbookError('Något gick fel.');
    } finally {
      setSavingLogbookId(null);
    }
  };

  const closeLogbookDeleteDialog = () => {
    if (logbookDeletingId) return;
    setLogbookDeleteDialogOpen(false);
    setLogbookDeleteTargetId(null);
    setLogbookDeletePreview(null);
    setLogbookDeletePreviewError(null);
    setLogbookDeletePreviewLoading(false);
  };

  const openLogbookDeleteDialog = async (logbookId) => {
    setLogbookError(null);
    setLogbookSuccess(null);
    setLogbookDeleteTargetId(logbookId);
    setLogbookDeleteDialogOpen(true);
    setLogbookDeletePreview(null);
    setLogbookDeletePreviewError(null);
    setLogbookDeletePreviewLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/logbooks/${logbookId}/delete-preview`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setLogbookDeletePreview({
          tripLogCount: typeof data.tripLogCount === 'number' ? data.tripLogCount : 0,
          serviceLogCount: typeof data.serviceLogCount === 'number' ? data.serviceLogCount : 0
        });
      } else {
        setLogbookDeletePreviewError(data.message || 'Kunde inte hämta information om loggboken.');
      }
    } catch (err) {
      setLogbookDeletePreviewError('Något gick fel.');
    } finally {
      setLogbookDeletePreviewLoading(false);
    }
  };

  const confirmLogbookDelete = async () => {
    if (!logbookDeleteTargetId || !logbookDeletePreview) return;
    setLogbookDeletingId(logbookDeleteTargetId);
    setLogbookError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/logbooks/${logbookDeleteTargetId}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setLogbookSuccess(data.message || 'Loggboken har tagits bort.');
        closeLogbookDeleteDialog();
        await refreshLogbooks();
      } else {
        setLogbookError(data.message || 'Kunde inte ta bort loggboken.');
      }
    } catch (err) {
      setLogbookError('Något gick fel.');
    } finally {
      setLogbookDeletingId(null);
    }
  };

  const handleRemoveExistingImage = (event, indexToRemove) => {
    event.preventDefault();
    
    // Använd profilePictures state direkt
    const currentPictures = Array.isArray(profilePictures) ? profilePictures : [];
    if (currentPictures.length === 0) return;
    
    const imageToRemove = currentPictures[0];
    setImagesToRemove(prev => [...prev, imageToRemove]);
    setProfilePictures([]);
  };

  const handleUploadSuccess = (files) => {
    setSelectedFiles(prevFiles => [...prevFiles, ...files]);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    
    // Om lösenordet är tomt, rensa fel
    if (newPassword.length === 0) {
      setPasswordError('');
      setPasswordStrength('');
      setError(null);
      return;
    }
    
    // Validera lösenord om det anges
    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      setPasswordError(errors.join(', '));
      setPasswordStrength('weak');
    } else {
      setPasswordError('');
      setPasswordStrength(getPasswordStrength(newPassword));
    }
    
    // Kontrollera matchning
    if (confirmPassword && newPassword !== confirmPassword) {
      setError('Lösenorden matchar inte');
    } else {
      setError(null);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    
    if (newConfirmPassword && newConfirmPassword !== password) {
      setError('Lösenorden matchar inte');
    } else {
      setError(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    // Validera lösenord om det anges
    if (password) {
      const passwordErrors = validatePassword(password);
      if (passwordErrors.length > 0) {
        setError(`Lösenordet uppfyller inte kraven: ${passwordErrors.join(', ')}`);
        return;
      }

      // Kontrollera att lösenorden matchar
      if (password !== confirmPassword) {
        setError('Lösenorden matchar inte');
        return;
      }
    }

    // Upload the files
    try {
      // Om inga nya bilder valts och inga bilder ska tas bort, spara direkt
      if (selectedFiles.length === 0 && imagesToRemove.length === 0) {
        const userData = {
          username,
          userid,
          email,
          firstName,
          lastName,
          bio,
          profilePictures: profilePicturesArray
        };

        // Om lösenordet inte är tomt, lägg till det i userData
        if (password.trim() !== '') {
          userData.password = password;
        }

        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/changeprofile/${userid}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...(getAuthHeader() ? { Authorization: getAuthHeader() } : {}) },
          body: JSON.stringify(userData)
        });

        const data = await response.text();
        console.log(data);
        navigate(`/showprofile/${userid}`);
        return;
      }

      const formData = new FormData();
      // Ta bara den första filen om flera valts
      if (selectedFiles.length > 0) {
        formData.append('file0', selectedFiles[0]);
      }


      const uploadResponse = await axios.post(`${getUploadApiBaseUrl()}/api/uploads/profile-pictures`, formData, {
        headers: getAuthHeader() ? { Authorization: getAuthHeader() } : {},
      });

      if (uploadResponse.status === 200) {
        const urls = (uploadResponse.data || []).map(f => f.url).filter(Boolean);
        const imagesToRemoveOnUpload = [];
        if (profilePicturesArray.length > 0 && selectedFiles.length > 0) {
          imagesToRemoveOnUpload.push(profilePicturesArray[0]);
        }
        const updatedImages = urls.length > 0 ? [urls[0]] : profilePicturesArray;

        // Skapa ett objekt med uppdaterade data att skicka till servern
        const userData = {
          username,
          userid,
          email,
          firstName,
          lastName,
          bio,
          profilePictures: updatedImages
        };

        // Om lösenordet inte är tomt, lägg till det i userData
        if (password.trim() !== '') {
          userData.password = password;
        }

        // Ta bort gamla bilder som ska tas bort (både från imagesToRemove och imagesToRemoveOnUpload)
        const allImagesToRemove = [...imagesToRemove];
        if (imagesToRemoveOnUpload.length > 0) {
          allImagesToRemove.push(...imagesToRemoveOnUpload);
        }
        
        allImagesToRemove.forEach(urlOrPath => {
          fetch(`${getUploadApiBaseUrl()}/api/deleteimage`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', ...(getAuthHeader() ? { Authorization: getAuthHeader() } : {}) },
            body: JSON.stringify({ url: urlOrPath })
          })
            .then(response => response.text())
            .then(data => {
              console.log(data);
            })
            .catch((error) => {
              console.error('Error:', error);
            });
        });
        console.log('user_id: ', userid);
        console.log('userData: ', userData);
        //skicka till servern
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/changeprofile/${userid}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...(getAuthHeader() ? { Authorization: getAuthHeader() } : {}) },
          body: JSON.stringify(userData)
        });

        const data = await response.text();
        console.log(data);
        navigate(`/showprofile/${userid}`); // Navigera till ShowProfile efter en lyckad uppdatering
      } else {
        console.error('Fel vid filuppladdning.');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Säkerställ att profilePictures är en array
  const profilePicturesArray = Array.isArray(profilePictures) ? profilePictures : [];
  const mainProfilePicture = profilePicturesArray.length > 0 ? profilePicturesArray[0] : null;

  const logbookDeleteTargetLb = ownedLogbooks.find((l) => l._id === logbookDeleteTargetId);
  const logbookDeleteDisplayName = logbookDeleteTargetLb
    ? logbookDeleteTargetLb.name || 'Loggbok utan namn'
    : 'Loggboken';

  return (
    <LogTheme>
      <Box sx={{ maxWidth: '800px', margin: '0 auto', padding: { xs: 2, md: 4 } }}>
        <Card sx={{ padding: 4 }}>
          <CardContent>
            <Typography variant="h4" component="h1" gutterBottom>
              Ändra din profil
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 4 }}>
              Uppdatera din profilinformation och profilbild
            </Typography>

            <Divider sx={{ mb: 4 }} />

            <form onSubmit={handleSubmit}>
              {/* Profile Picture Section */}
              <Box mb={4}>
                <Typography variant="h6" gutterBottom>
                  Profilbild
                </Typography>
                <Box display="flex" justifyContent="center" mb={3}>
                  {mainProfilePicture ? (
                    <Avatar
                      src={getImageDisplayUrl(mainProfilePicture)}
                      alt="Profilbild"
                      sx={{ width: 150, height: 150 }}
                    />
                  ) : (
                    <AccountCircleIcon sx={{ fontSize: 150, color: 'action.disabled' }} />
                  )}
                </Box>
                <Box mb={2} display="flex" flexDirection="column" alignItems="center" gap={2}>
                  <ImageUploader onUploadSuccess={handleUploadSuccess} />
                  {mainProfilePicture && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={(event) => {
                        event.preventDefault();
                        if (profilePicturesArray.length > 0) {
                          handleRemoveExistingImage(event, 0);
                        }
                      }}
                    >
                      Ta bort profilbild
                    </Button>
                  )}
                </Box>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Profile Information Section */}
              <Box mb={4}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Profilinformation
                </Typography>
                <Box mb={3}>
                  <TextField
                    type="email"
                    label="E-post"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    fullWidth
                    disabled
                    helperText="E-postadressen kan inte ändras"
                  />
                </Box>
                <Box mb={3}>
                  <TextField
                    type="text"
                    label="Namn"
                    value={`${firstName || ''} ${lastName || ''}`.trim() || ''}
                    onChange={(e) => {
                      const names = e.target.value.split(' ');
                      setFirstName(names[0] || '');
                      setLastName(names.slice(1).join(' ') || '');
                    }}
                    fullWidth
                    helperText="Förnamn och efternamn"
                  />
                </Box>
                <Box mb={3}>
                  <TextField
                    type="text"
                    label="Användarnamn"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    fullWidth
                  />
                </Box>
                <Box mb={3}>
                  <TextField
                    multiline
                    rows={4}
                    label="Beskrivning"
                    placeholder="Skriv lite om båten och besättningen ..."
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    fullWidth
                  />
                </Box>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Password Section */}
              <Box mb={4}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                  Lösenord
                </Typography>
                <Box mb={2}>
                  <TextField
                    type={showPassword ? 'text' : 'password'}
                    label="Lösenord (lämna tomt för att behålla nuvarande)"
                    value={password}
                    onChange={handlePasswordChange}
                    error={!!passwordError}
                    helperText={
                      passwordError 
                        ? passwordError 
                        : passwordStrength === 'strong' 
                          ? 'Starkt lösenord ✓' 
                          : passwordStrength === 'medium' 
                            ? 'Medelstarkt lösenord' 
                            : passwordStrength === 'good' 
                              ? 'Bra lösenord' 
                              : password 
                                ? 'Lösenordet måste innehålla: minst 8 tecken, stor bokstav, liten bokstav, siffra och specialtecken'
                                : ''
                    }
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                {password && (
                  <Box mb={2}>
                    <TextField
                      type={showConfirmPassword ? 'text' : 'password'}
                      label="Bekräfta lösenord"
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      error={!!error && error.includes('matchar inte')}
                      helperText={error && error.includes('matchar inte') ? error : ''}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                )}
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Mina loggböcker - byt namn */}
              {ownedLogbooks.length > 0 && (
                <Box mb={4}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                    Mina loggböcker
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Du kan ge dina loggböcker namn som andra ser när de är inbjudna (t.ex. båtens namn eller familjens namn).
                    {ownedLogbooks.length > 1 && (
                      <> Om du av misstag har skapat en loggbok som inte behövs, kan du ta bort den genom att klicka på &quot;Ta bort loggbok&quot;.</>
                    )}
                  </Typography>
                  {logbookSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setLogbookSuccess(null)}>
                      {logbookSuccess}
                    </Alert>
                  )}
                  {logbookError && (
                    <Typography color="error" sx={{ mb: 2 }}>{logbookError}</Typography>
                  )}
                  {ownedLogbooks.map((lb) => (
                    <Box
                      key={lb._id}
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 2,
                        mb: 2
                      }}
                    >
                      <TextField
                        size="small"
                        label="Namn på loggbok"
                        value={logbookNames[lb._id] ?? (lb.name || '')}
                        onChange={(e) => handleLogbookNameChange(lb._id, e.target.value)}
                        placeholder="t.ex. båtens namn"
                        sx={{ flex: '1 1 200px', minWidth: 0 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleSaveLogbookName(lb._id)}
                        disabled={savingLogbookId === lb._id}
                      >
                        {savingLogbookId === lb._id ? 'Sparar...' : 'Spara namn'}
                      </Button>
                      {ownedLogbooks.length > 1 && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => openLogbookDeleteDialog(lb._id)}
                          disabled={!!logbookDeletingId}
                        >
                          Ta bort loggbok
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Submit Button */}
              <Box display="flex" justifyContent="flex-end" mt={4}>
                <Button type="submit" variant="contained" color="primary" size="large">
                  Spara ändringar
                </Button>
              </Box>

            </form>
            {error && !error.includes('matchar inte') && (
              <Box mt={2}>
                <Typography color="error">{error}</Typography>
              </Box>
            )}

            <Dialog open={logbookDeleteDialogOpen} onClose={closeLogbookDeleteDialog} fullWidth maxWidth="sm">
              <DialogTitle>Ta bort loggbok</DialogTitle>
              <DialogContent>
                {logbookDeletePreviewLoading && (
                  <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={32} />
                  </Box>
                )}
                {!logbookDeletePreviewLoading && logbookDeletePreviewError && (
                  <Typography color="error">{logbookDeletePreviewError}</Typography>
                )}
                {!logbookDeletePreviewLoading && !logbookDeletePreviewError && logbookDeletePreview && (
                  <Typography variant="body1" component="div">
                    {logbookDeletePreview.tripLogCount + logbookDeletePreview.serviceLogCount > 0 ? (
                      <>
                        Är du säker? Loggboken du försöker ta bort ({logbookDeleteDisplayName}) innehåller tripp- eller
                        serviceloggar. Allt sådant innehåll tas bort permanent. Åtgärden kan inte ångras.
                      </>
                    ) : (
                      <>
                        Är du säker på att du vill ta bort loggboken &quot;{logbookDeleteDisplayName}&quot;? Åtgärden kan
                        inte ångras.
                      </>
                    )}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={closeLogbookDeleteDialog} disabled={!!logbookDeletingId}>
                  Avbryt
                </Button>
                <Button
                  onClick={confirmLogbookDelete}
                  color="error"
                  variant="contained"
                  disabled={
                    !!logbookDeletePreviewLoading ||
                    !!logbookDeletePreviewError ||
                    !logbookDeletePreview ||
                    !!logbookDeletingId
                  }
                >
                  {logbookDeletingId ? 'Tar bort...' : 'Ta bort loggbok'}
                </Button>
              </DialogActions>
            </Dialog>
          </CardContent>
        </Card>
      </Box>
    </LogTheme>
  );
}

export default ChangeProfile;